import { barTip, el, replaceChildrenWith } from "./dom";
import { integer, languageName } from "./format";
import {
  Bucket,
  DEFAULT_INTERVAL,
  INTERVALS,
  IntervalMinutes,
  ItemRef,
  NO_LANGUAGE,
  SectionCount,
  TimelineGrid,
  blockWord,
  browserOffsetMinutes,
  buildGrid,
  clockMeridiem,
  itemsById,
  longDate,
  markWord,
  parseUtc,
  phrase,
  sectionCounts
} from "./timeline";
import type { SpeakerParticipationReport, Timeline } from "./types";

/**
 * AB#9981. The two time sections of the report and the control that sizes their blocks.
 *
 * <b>One control, both tables.</b> The 15 / 30 / 60 minute radiogroup rebuilds the grid and
 * redraws everything downstream of it, headings included -- "by half hour" is not a caption,
 * it is a statement about the numbers underneath, and leaving it behind when the blocks
 * change is the way a report comes to describe a meeting that did not happen.
 *
 * <b>Nothing here builds markup from a string.</b> Agenda titles and customer-configured
 * status names reach this file, and both are text a member of the public or a clerk typed
 * into a form. Every node is constructed and every value goes through `textContent`.
 */

const STORAGE_KEY = "spr.timeline.interval";

/**
 * One render, one set of element ids.
 *
 * The entry point below already reasons that "two insights on one page are two independent
 * renders of the same report" -- and every id that goes with a render used to be a global
 * constant. Two renders then meant two elements sharing an id, which is not a cosmetic
 * problem: aria-labelledby and aria-describedby resolve to the FIRST match, so the second
 * chart announces the first chart's description and the second radiogroup borrows the first
 * one's label. That is a WCAG 4.1.1 failure that reads as working software.
 */
let renderSequence = 0;

/** Series drawn on the listen-in chart before the rest are folded into one. Four lines is
 *  what a reader can follow; a legend of nine is a table with extra steps. */
const MAX_SERIES = 4;

const SVG_NS = "http://www.w3.org/2000/svg";

/** Ellipsis width for an agenda title in the popover, matched by `.spr-ai-title` in CSS. */
const TITLE_TRUNCATE_HINT = "28ch";

export interface TimelineOptions {
  report: SpeakerParticipationReport;
  timeline: Timeline;
  /** The TimeProfile insight's root. `data-interval` is published here so a test can wait
   *  on the interval having been APPLIED rather than on the button having been clicked. */
  root: HTMLElement;
}

// -- entry ------------------------------------------------------------------

/**
 * Builds both time blocks and returns them in render order.
 *
 * The interval lives in this closure rather than in a module variable: two insights on one
 * page are two independent renders of the same report, and a shared variable would make one
 * reader's radio button move the other's table.
 */
export function timelineSections(options: TimelineOptions): HTMLElement[] {
  const { report, timeline, root } = options;

  const items = itemsById(report);
  const sections = sectionCounts(report);
  const browserOffset = browserOffsetMinutes(report.meeting.startUtc);

  renderSequence += 1;
  const uid = "spr-" + renderSequence;
  const turnsHeadingId = uid + "-turns-h";
  const listenHeadingId = uid + "-listen-h";

  const turnsBody = el("div", { class: "spr-timeline-body" });
  const listenBody = el("div", { class: "spr-timeline-body" });
  const turnsHeading = el("h2", { id: turnsHeadingId });
  const listenHeading = el("h2", { id: listenHeadingId });
  const turnsSub = el("p", { class: "spr-sub" });
  const listenSub = el("p", { class: "spr-sub" });
  const status = el("div", {
    class: "spr-live",
    role: "status",
    "aria-live": "polite",
    "aria-atomic": "true"
  });

  let interval = storedInterval();

  const control = intervalControl(uid, interval, (next) => {
    interval = next;
    remember(next);
    draw();
    status.textContent = "Time blocks are now " + next + " minutes.";
  });

  function draw(): void {
    const grid = buildGrid(timeline, report.meeting.startUtc, interval, browserOffset);
    root.setAttribute("data-interval", String(interval));

    const zoneSuffix = grid.offsetFromServer ? "" : " (browser time zone)";
    turnsHeading.textContent = "Connections and speaking turns " + phrase(interval) + zoneSuffix;
    listenHeading.textContent = "Listen-in lines " + phrase(interval) + zoneSuffix;

    const subtitle = meetingSubtitle(report, timeline, grid);
    turnsSub.textContent = subtitle;
    listenSub.textContent = subtitle;

    replaceChildrenWith(turnsBody, turnsContent(uid, report, grid, interval, items, sections));
    replaceChildrenWith(listenBody, listenContent(uid, grid, interval));
  }

  const turnsBlock = el("section", {
    class: "spr-block",
    "data-testid": "speaker-report-turns-by-block",
    "aria-labelledby": turnsHeadingId
  }, [
    el("div", { class: "spr-block-head" }, [turnsHeading, turnsSub]),
    control,
    status,
    turnsBody
  ]);

  const listenBlock = el("section", {
    class: "spr-block",
    "data-testid": "speaker-report-listen-in",
    "aria-labelledby": listenHeadingId
  }, [
    el("div", { class: "spr-block-head" }, [listenHeading, listenSub]),
    listenBody
  ]);

  draw();

  return [turnsBlock, listenBlock];
}

function meetingSubtitle(
  report: SpeakerParticipationReport,
  timeline: Timeline,
  grid: TimelineGrid
): string {
  const parts: string[] = [];
  if (report.meeting.name) parts.push(report.meeting.name);

  const anchor = grid.buckets.length > 0
    ? grid.buckets[0].startMs
    : parseUtc(report.meeting.startUtc);
  if (anchor !== null) {
    parts.push(longDate(anchor, grid.offsetMinutes));
  }

  parts.push(grid.offsetFromServer && timeline.timeZoneId
    ? "times " + timeline.timeZoneId
    : "times shown in your browser's time zone");

  return parts.join(" · ");
}

// -- the interval control ---------------------------------------------------

function storedInterval(): IntervalMinutes {
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    for (const candidate of INTERVALS) {
      if (candidate === stored) return candidate;
    }
  } catch {
    // Private browsing, or storage disabled by policy. The default is not worth an error.
  }
  return DEFAULT_INTERVAL;
}

function remember(interval: IntervalMinutes): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(interval));
  } catch {
    // As above. The reader's choice not surviving a reload is a smaller failure than the
    // report not rendering.
  }
}

/**
 * A radiogroup, not a set of buttons.
 *
 * Three controls where exactly one is chosen IS a radio group, and the difference is
 * audible: a screen reader announces "30 min, radio button, checked, 2 of 3" rather than
 * three unrelated buttons. Roving tabindex and arrow keys come with the role, so they are
 * implemented rather than left to the reader to discover they are missing.
 */
function intervalControl(
  uid: string,
  current: IntervalMinutes,
  onChange: (interval: IntervalMinutes) => void
): HTMLElement {
  const labelId = uid + "-iv-label";
  const label = el("span", { class: "spr-iv-label", id: labelId, text: "Block size" });

  const options: HTMLButtonElement[] = INTERVALS.map((value) =>
    el("button", {
      type: "button",
      class: "spr-iv-opt",
      role: "radio",
      "data-interval-option": value,
      "aria-checked": value === current ? "true" : "false",
      tabindex: value === current ? "0" : "-1",
      text: value + " min"
    }));

  let chosen = current;

  function select(next: IntervalMinutes, moveFocus: boolean): void {
    if (next === chosen) return;
    chosen = next;
    options.forEach((option, index) => {
      const isChosen = INTERVALS[index] === next;
      option.setAttribute("aria-checked", isChosen ? "true" : "false");
      option.setAttribute("tabindex", isChosen ? "0" : "-1");
      if (isChosen && moveFocus) option.focus();
    });
    onChange(next);
  }

  options.forEach((option, index) => {
    option.addEventListener("click", () => select(INTERVALS[index], false));
    option.addEventListener("keydown", (event: KeyboardEvent) => {
      const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1
          : 0;
      if (step === 0) return;
      event.preventDefault();
      const next = (index + step + INTERVALS.length) % INTERVALS.length;
      select(INTERVALS[next], true);
    });
  });

  return el("div", { class: "spr-iv-bar" }, [
    label,
    el("div", {
      class: "spr-iv-seg",
      role: "radiogroup",
      "aria-labelledby": labelId,
      "data-testid": "speaker-report-interval"
    }, options),
    el("span", {
      class: "spr-fine",
      // What it actually does. The choice is kept in localStorage under one key, so it
      // applies to every speaker report opened in this browser -- not only this one. A
      // sentence that claims less than that leaves a clerk wondering why last week's
      // meeting is suddenly drawn in quarter-hours.
      text: "Sets the block size for both time tables, on screen and in print, "
        + "and is remembered for the reports you open on this device."
    })
  ]);
}

// -- connections and speaking turns -----------------------------------------

function turnsContent(
  uid: string,
  report: SpeakerParticipationReport,
  grid: TimelineGrid,
  interval: IntervalMinutes,
  items: Record<number, ItemRef>,
  sections: SectionCount[]
): HTMLElement[] {
  if (grid.buckets.length === 0) {
    return [el("p", {
      class: "spr-notice spr-notice-quiet",
      text: "No speaking turn or caller leg on this meeting carries a usable timestamp, so "
        + "there is nothing to place on a clock."
    })];
  }

  const busiest = grid.buckets.reduce((most, bucket) => Math.max(most, bucket.turns), 0);
  const peakIndex = grid.buckets.findIndex((bucket) => bucket.turns === busiest && busiest > 0);

  const popover = activationPopover(uid);
  const activationRecorded = report.timeProfile.timeline.activationSource === "StatusHistory";

  const rows = grid.buckets.map((bucket, index) =>
    turnsRow(bucket, index === peakIndex, busiest || 1, items, sections, popover, activationRecorded));

  const totalRecorded = grid.buckets.reduce((sum, b) => sum + b.recorded.length, 0);
  const totalInferred = grid.buckets.reduce((sum, b) => sum + b.inferred.length, 0);

  // Turns stamped outside the drawn window get a row of their own rather than being
  // dropped, so the block rows plus this row equal the All-day total. They are usually a
  // stale timer press on an old record; the reader can see that, and see the count.
  if (grid.outsideWindow.turns > 0) {
    rows.push(el("div", {
      class: "spr-row spr-row-quiet",
      role: "row",
      "data-testid": "speaker-report-outside-window",
      "data-outside-turns": grid.outsideWindow.turns
    }, [
      el("div", { class: "spr-cell", role: "rowheader", text: "Outside the meeting window" }),
      numCell(grid.outsideWindow.inPerson),
      numCell(grid.outsideWindow.byPhone),
      numCell(grid.outsideWindow.turns),
      numCell(grid.outsideWindow.speakers),
      el("div", { class: "spr-cell spr-num", role: "gridcell", text: "\u2014" }),
      el("div", { class: "spr-cell", role: "gridcell" }, [
        el("span", { class: "spr-bar-note", text: "In no block" })
      ])
    ]));
  }

  rows.push(el("div", { class: "spr-row spr-row-total", role: "row", "data-timeline-total": "" }, [
    el("div", { class: "spr-cell", role: "rowheader", text: "All day" }),
    numCell(grid.totalInPerson),
    numCell(grid.totalByPhone),
    numCell(grid.totalTurns, "spr-total"),
    numCell(grid.totalSpeakers),
    el("div", {
      class: "spr-cell spr-num", role: "gridcell",
      text: totalRecorded + "/" + totalInferred
    }),
    el("div", { class: "spr-cell", role: "gridcell" }, [
      el("span", {
        class: "spr-bar-note",
        text: integer(grid.totalTurns) + " of " + integer(report.totals.signUps) + " sign-ups spoke"
      })
    ])
  ]));

  const grid_ = el("div", {
    class: "spr-grid spr-grid-timeline",
    role: "grid",
    // Focusable BECAUSE it scrolls. Below 720px the rows carry a min-width, so this box is
    // a real horizontal scroll container -- and a scroll container that is not focusable
    // cannot be panned by a keyboard-only or switch reader at all (WCAG 2.1.1).
    tabindex: "0",
    "aria-label": "Connections and speaking turns by block. Scrolls sideways.",
    "data-testid": "speaker-report-timeline-grid"
  }, [
    el("div", { class: "spr-row spr-row-head", role: "row" }, [
      headCell(blockWord(interval)),
      headCell("In person", true),
      headCell("By phone", true),
      headCell("Turns", true),
      headCell("Speakers", true),
      headCell("Active items", true),
      headCell("Distribution")
    ]),
    ...rows
  ]);

  const shell = el("div", { class: "spr-timeline-shell" }, [grid_, popover.node]);

  const out: HTMLElement[] = [
    el("h3", {
      class: "spr-section-title",
      text: "Speakers " + phrase(interval)
    }),
    shell,
    el("ul", { class: "spr-legend" }, [
      swatch("spr-swatch-in-person", "In person"),
      swatch("spr-swatch-phone", "By phone"),
      el("li", {
        class: "spr-legend-item",
        text: "Bar lengths are counts against the busiest "
          + blockWord(interval).toLowerCase() + " (" + integer(busiest) + ")."
      })
    ]),
    el("p", {
      class: "spr-fine",
      text: "Active items shows recorded and inferred item counts as recorded/inferred. "
        + "Hover or focus a time block to list the items."
    }),
    el("p", {
      class: "spr-fine",
      "data-testid": "speaker-report-activation-legend",
      text: activationRecorded
        ? "Recorded: status change logged by the platform. Inferred: derived from speaking turns."
        : "Activation: not recorded for this meeting. Every item shown is inferred from "
          + "speaking turns."
    }),
    el("p", {
      class: "spr-fine",
      text: "A speaker is placed in the time block their turn started, and counts once per "
        + "block, so someone who speaks in two blocks appears in both. Sign-ups that never "
        + "spoke appear in no block."
    }),
    el("p", { class: "spr-fine", text: channelCoverageNote(report.timeProfile.timeline) })
  ];

  if (grid.outsideWindow.turns > 0) {
    out.push(el("p", {
      class: "spr-fine",
      "data-testid": "speaker-report-window-note",
      text: "The grid covers the day the meeting started. "
        + integer(grid.outsideWindow.turns)
        + (grid.outsideWindow.turns === 1 ? " turn is" : " turns are")
        + " stamped outside it -- usually the speaking timer pressed on a stale record -- "
        + "so they are counted in the row above and in the All day total, and drawn in no "
        + "block."
    }));
  }

  if (grid.truncated) {
    out.push(el("p", {
      class: "spr-notice spr-notice-quiet",
      text: "The grid was cut after " + integer(grid.buckets.length) + " blocks. A meeting "
        + "reaching past that has a timestamp in it that is not a meeting instant."
    }));
  }

  return out;
}

function turnsRow(
  bucket: Bucket,
  isPeak: boolean,
  busiest: number,
  items: Record<number, ItemRef>,
  sections: SectionCount[],
  popover: Popover,
  activationRecorded: boolean
): HTMLElement {
  // A BUTTON, not a focusable span.
  //
  // AGENTS.md is explicit that an interactive non-button needs role="button", tabindex and
  // an Enter/Space handler; a real button brings all three and the correct announcement with
  // it, and it is what makes the popover openable without a pointer. aria-describedby is NOT
  // set here -- see activationPopover: one shared tooltip node means a permanent reference
  // would describe every block with whatever block was hovered last.
  const trigger = el("button", {
    type: "button",
    class: "spr-ai-trigger",
    "aria-expanded": "false",
    "data-testid": "speaker-report-activation-trigger",
    text: bucket.label
  });

  popover.bind(trigger, bucket, items, sections, activationRecorded);

  const percent = (value: number) => (busiest === 0 ? 0 : (value / busiest) * 100).toFixed(1) + "%";

  // Each segment names itself on hover against the block's own total; the group carries both
  // for a reader who cannot hover. Lengths stay scaled to the busiest block, as the legend says.
  const blockTotal = bucket.inPerson + bucket.byPhone;
  const inPersonTip = barTip("In person", bucket.inPerson, blockTotal);
  const byPhoneTip = barTip("By phone", bucket.byPhone, blockTotal);

  const distribution = bucket.turns === 0
    ? el("span", { class: "spr-bar-note", text: "No speakers" })
    : el("span", {
      class: "spr-bar-stack spr-bar-group",
      role: "img",
      "aria-label": bucket.label + ": " + inPersonTip + "; " + byPhoneTip
    }, [
      bucket.inPerson > 0
        ? el("span", { class: "spr-bar-in-person", title: inPersonTip, style: "--spr-fill:" + percent(bucket.inPerson) })
        : null,
      bucket.byPhone > 0
        ? el("span", { class: "spr-bar-phone", title: byPhoneTip, style: "--spr-fill:" + percent(bucket.byPhone) })
        : null
    ].filter(Boolean) as HTMLElement[]);

  return el("div", {
    class: "spr-row" + (bucket.turns === 0 ? " spr-row-quiet" : ""),
    role: "row",
    "data-timeline-block": bucket.markLabel,
    "data-turns": bucket.turns,
    "data-speakers": bucket.speakers
  }, [
    el("div", { class: "spr-cell", role: "rowheader" }, [
      trigger,
      isPeak && bucket.turns > 0
        ? el("span", { class: "spr-chip spr-chip-cap", text: "Most turns" })
        : null
    ].filter(Boolean) as HTMLElement[]),
    numCell(bucket.inPerson),
    numCell(bucket.byPhone),
    numCell(bucket.turns),
    numCell(bucket.speakers),
    el("div", {
      class: "spr-cell spr-num",
      role: "gridcell",
      title: bucket.recorded.length + " recorded, " + bucket.inferred.length + " inferred"
    }, [
      document.createTextNode(bucket.recorded.length + "/" + bucket.inferred.length),
      // The popover is a hover affordance and hover does not print. The same list is
      // repeated here as text that only the printed sheet shows, so a report folded into a
      // Board packet does not lose the column it spends a paragraph explaining.
      printableActiveItems(bucket, items, sections)
    ]),
    el("div", { class: "spr-cell", role: "gridcell" }, [distribution])
  ]);
}

function printableActiveItems(
  bucket: Bucket,
  items: Record<number, ItemRef>,
  sections: SectionCount[]
): HTMLElement {
  const ids = bucket.recorded.concat(bucket.inferred);
  const text = ids.length === 0
    ? (sections.length > 0
      ? "No activation recorded. " + sections.map((s) => itemsIn(s.count, s.title)).join("; ")
      : "No activation recorded.")
    : ids.length > 6
      ? groupBySection(ids, items).map((group) => itemsIn(group.count, group.title)).join("; ")
      : ids.map((id) => "Item " + itemNumber(items, id)).join(", ");

  return el("span", { class: "spr-print-only spr-ai-print", text });
}

// -- the activation popover -------------------------------------------------

interface Popover {
  id: string;
  node: HTMLElement;
  bind: (
    trigger: HTMLElement,
    bucket: Bucket,
    items: Record<number, ItemRef>,
    sections: SectionCount[],
    activationRecorded: boolean
  ) => void;
}

/**
 * ONE tooltip node for the whole table, moved and refilled.
 *
 * A popover per row is forty-eight hidden subtrees a screen reader has to walk past, and
 * forty-eight copies of every agenda title in the DOM. One node also means one Escape
 * handler and one thing that can be open, which is the behaviour a reader expects.
 */
function activationPopover(uid: string): Popover {
  const id = uid + "-activation-pop";
  const node = el("div", {
    class: "spr-ai-pop",
    id,
    role: "tooltip",
    "data-testid": "speaker-report-activation-popover",
    hidden: true
  });

  let openFor: HTMLElement | null = null;

  function hide(): void {
    node.hidden = true;

    // Dropped, not left pointing at a hidden node. A node referenced by aria-describedby is
    // part of the accessible description even while hidden, so a permanent reference would
    // make every trigger in the table advertise the last block anyone hovered.
    if (openFor) {
      openFor.removeAttribute("aria-describedby");
      openFor.setAttribute("aria-expanded", "false");
    }

    openFor = null;
  }

  function show(trigger: HTMLElement, children: Array<Node | null>): void {
    const shell = node.parentElement;

    // Measured BEFORE the node is refilled. Neither box depends on the popover's contents,
    // and reading them after a mutation forces a synchronous re-layout of the whole grid --
    // once per row for a pointer travelling down the block column.
    const shellBox = shell ? shell.getBoundingClientRect() : null;
    const triggerBox = trigger.getBoundingClientRect();

    if (openFor && openFor !== trigger) {
      openFor.removeAttribute("aria-describedby");
      openFor.setAttribute("aria-expanded", "false");
    }

    replaceChildrenWith(node, children);
    node.hidden = false;
    openFor = trigger;
    trigger.setAttribute("aria-describedby", id);
    trigger.setAttribute("aria-expanded", "true");

    if (!shell || !shellBox) return;

    const left = Math.max(0, Math.min(
      triggerBox.left - shellBox.left,
      Math.max(0, shell.clientWidth - node.offsetWidth)));
    node.style.left = left + "px";
    node.style.top = (triggerBox.bottom - shellBox.top + 6) + "px";
  }

  return {
    id,
    node,
    bind: (trigger, bucket, items, sections, activationRecorded) => {
      const open = () => show(trigger, popoverCard(bucket, items, sections, activationRecorded));
      trigger.addEventListener("mouseenter", open);
      trigger.addEventListener("focus", open);
      trigger.addEventListener("click", open);
      trigger.addEventListener("mouseleave", hide);
      trigger.addEventListener("blur", hide);

      // Escape is handled on the TRIGGER, not on the document.
      //
      // Changing the interval rebuilds this whole table, so a document-level listener would
      // be added once per redraw and would hold every discarded row alive through its
      // closure. The trigger is also where focus actually is when a keyboard reader wants to
      // dismiss the tooltip, so nothing is lost -- and focus stays put rather than needing
      // to be restored.
      trigger.addEventListener("keydown", (event: KeyboardEvent) => {
        if (event.key !== "Escape" || node.hidden || openFor !== trigger) return;
        event.stopPropagation();
        hide();
      });
    }
  };
}

function popoverCard(
  bucket: Bucket,
  items: Record<number, ItemRef>,
  sections: SectionCount[],
  activationRecorded: boolean
): Array<Node | null> {
  const body: HTMLElement[] = [];

  // Recorded only when the platform actually wrote the history. An empty list from a real
  // source and an empty list with no source are different facts, and only the enum knows.
  if (activationRecorded && bucket.recorded.length > 0) {
    body.push(el("div", { class: "spr-ai-sec" }, [
      chip("Recorded", "spr-chip-ok"),
      el("span", { class: "spr-ai-sec-t", text: "status changed" })
    ]));
    body.push(itemList(bucket.recorded, items, bucket));
  }

  if (bucket.inferred.length > 0) {
    body.push(el("div", { class: "spr-ai-sec" }, [
      chip("Inferred from speaking turns", "spr-chip-quiet")
    ]));
    body.push(itemList(bucket.inferred, items, null));
  }

  if (body.length === 0) {
    body.push(el("p", { class: "spr-ai-empty", text: "No activation recorded" }));

    // Section counts are the only thing left worth saying: they tell the reader what the
    // block COULD have covered. Omitted rather than faked when the payload carries no
    // agenda tree.
    if (sections.length > 0) {
      body.push(el("div", { class: "spr-ai-sec" }, [chip("Section", "spr-chip-quiet")]));
      body.push(el("ul", { class: "spr-ai-list" }, sections.map((section) =>
        el("li", {}, [
          el("span", {
            class: "spr-ai-group",
            text: itemsIn(section.count, section.title)
          })
        ]))));
    }
  }

  return [
    el("div", { class: "spr-ai-head" }, [el("strong", { text: bucket.label })]),
    el("div", { class: "spr-ai-body" }, body)
  ];
}

function itemList(
  ids: number[],
  items: Record<number, ItemRef>,
  bucket: Bucket | null
): HTMLElement {
  // Above six the list stops being a list and becomes a wall. Sections are what a reader
  // recognises at that size, so the card switches to counting them.
  if (ids.length > 6) {
    return el("ul", { class: "spr-ai-list" }, groupBySection(ids, items).map((group) =>
      el("li", {}, [
        el("span", { class: "spr-ai-group" }, [
          document.createTextNode(itemsIn(group.count, group.title)),
          el("span", {
            class: "spr-ai-hint",
            text: "Items " + group.numbers.slice(0, 3).join(", ")
              + (group.numbers.length > 3 ? " …" : "")
          })
        ])
      ])));
  }

  return el("ul", { class: "spr-ai-list" }, ids.map((id) => {
    const item = items[id];
    const title = item ? item.title : "";
    return el("li", {}, [
      el("span", { class: "spr-ai-n", text: "Item " + itemNumber(items, id) }),
      // Truncation is the stylesheet's job (max-width: 28ch, ellipsis). title= carries the
      // whole string so nothing is lost, and textContent is what puts it in the DOM.
      el("span", { class: "spr-ai-title", title, text: title, "data-truncate": TITLE_TRUNCATE_HINT }),
      bucket && bucket.alsoSpoke[id] ? chip("also spoke", "spr-chip-quiet") : null
    ].filter(Boolean) as HTMLElement[]);
  }));
}

interface SectionGroup {
  title: string;
  count: number;
  numbers: string[];
}

function groupBySection(ids: number[], items: Record<number, ItemRef>): SectionGroup[] {
  const groups: SectionGroup[] = [];
  for (const id of ids) {
    const item = items[id];
    const title = item ? item.sectionTitle : "Unsectioned";
    let group = groups.filter((g) => g.title === title)[0];
    if (!group) {
      group = { title, count: 0, numbers: [] };
      groups.push(group);
    }
    group.count++;
    group.numbers.push(itemNumber(items, id));
  }
  return groups;
}

function itemNumber(items: Record<number, ItemRef>, id: number): string {
  const item = items[id];
  if (!item) return String(id);
  return item.number || item.title || String(id);
}

// -- listen-in lines --------------------------------------------------------

function listenContent(uid: string, grid: TimelineGrid, interval: IntervalMinutes): HTMLElement[] {
  if (grid.buckets.length === 0) {
    return [el("p", {
      class: "spr-notice spr-notice-quiet",
      text: "No caller leg on this meeting carries a usable timestamp."
    })];
  }

  // The per-language split, and what is actually DRAWN. A meeting where every leg carries
  // the same language gets one unnamed series -- "es-MX lines" as the only line on a chart
  // says the language matters here, and it does not.
  const series = seriesFor(grid);
  const drawn: Series[] = series.length > 0
    ? series
    : [{ key: "all", label: "Lines at the mark", valueAt: (b: Bucket) => b.atMark }];
  const hasLines = grid.buckets.some((bucket) => bucket.atMark > 0 || bucket.lines > 0);

  if (!hasLines) {
    return [el("p", {
      class: "spr-notice spr-notice-quiet",
      text: "No public caller connected to the bridge on this meeting, so there are no "
        + "listen-in lines to chart."
    })];
  }

  const peak = grid.buckets.reduce((most, bucket) => Math.max(most, bucket.atMark), 0);
  let peakSeen = false;

  const rows = grid.buckets.map((bucket) => {
    const isPeak = !peakSeen && bucket.atMark === peak && peak > 0;
    if (isPeak) peakSeen = true;

    return el("div", {
      class: "spr-row",
      role: "row",
      "data-listen-block": bucket.markLabel,
      "data-at-mark": bucket.atMark
    }, [
      el("div", { class: "spr-cell", role: "rowheader" }, [
        document.createTextNode(bucket.markLabel),
        isPeak ? el("span", { class: "spr-chip spr-chip-cap", text: "Most lines" }) : null
      ].filter(Boolean) as Array<Node>),
      ...series.map((s) => numCell(s.valueAt(bucket))),
      numCell(bucket.atMark),
      numCell(bucket.lines),
      numCell(bucket.listenOnly)
    ]);
  });

  const table = el("div", {
    class: "spr-grid spr-grid-listen",
    role: "grid",
    // Focusable for the same reason the block grid is -- it scrolls sideways below 720px.
    tabindex: "0",
    "aria-label": "Listen-in lines by block. Scrolls sideways.",
    "data-testid": "speaker-report-listen-grid",
    "data-series": series.length
  }, [
    el("div", { class: "spr-row spr-row-head", role: "row" }, [
      headCell("Time"),
      ...series.map((s) => headCell(s.label, true)),
      headCell("Lines at the mark", true),
      headCell("Distinct lines in the block", true),
      headCell("Listen-only lines", true)
    ]),
    ...rows
  ]);

  return [
    el("h3", {
      class: "spr-section-title",
      text: blockWord(interval) + " counts"
    }),
    lineChart(uid, grid, drawn, interval),
    el("ul", { class: "spr-legend" }, drawn.map((s, index) =>
      swatch("spr-swatch-series-" + Math.min(index + 1, MAX_SERIES + 1), s.label))),
    table,
    el("p", {
      class: "spr-fine",
      text: "Lines at the mark is the number of public lines connected at the moment the "
        + "block opens. Distinct lines in the block counts every line connected at any point "
        + "inside that block. Listen-only lines are the lines connected inside the block "
        + "with no speaking turn starting in it. All three exclude host, interpretation and "
        + "room-audio legs, which are staff infrastructure present for the whole meeting."
    }),
    el("p", {
      class: "spr-fine",
      "data-testid": "speaker-report-open-legs",
      text: grid.openLegs === 0
        ? "Every caller has a recorded exit time."
        : (grid.openLegs === 1 ? "One caller has" : integer(grid.openLegs) + " callers have")
          + " no recorded exit and "
          + (grid.openLegs === 1 ? "is" : "are")
          + " treated as connected until "
          + clockMeridiem(grid.lastSeenMs, grid.offsetMinutes)
          + ", the last timestamp observed on this meeting."
    }),
    el("p", {
      class: "spr-fine",
      text: "Callers not on the line at a " + markWord(interval)
        + " mark still count in every block they overlap, so the two line columns disagree "
        + "whenever people join and leave inside one block."
    })
  ];
}

interface Series {
  key: string;
  label: string;
  valueAt: (bucket: Bucket) => number;
}

function seriesFor(grid: TimelineGrid): Series[] {
  // Codes are folded by DISPLAY name first: `en` and `en-US` legs are one "English" series,
  // not two lines with the same label.
  const groups: Array<{ label: string; codes: string[] }> = [];
  for (const code of grid.languages) {
    const label = code === NO_LANGUAGE ? "Language not recorded" : languageName(code) + " lines";
    let group = groups.filter((g) => g.label === label)[0];
    if (!group) {
      group = { label, codes: [] };
      groups.push(group);
    }
    group.codes.push(code);
  }

  const sumAt = (codes: string[], bucket: Bucket): number =>
    codes.reduce((sum, code) => sum + (bucket.atMarkByLanguage[code] ?? 0), 0);

  const totals = groups.map((group) => ({
    group,
    total: grid.buckets.reduce((sum, bucket) => sum + sumAt(group.codes, bucket), 0)
  }));
  totals.sort((a, b) => b.total - a.total);

  const named: Series[] = totals.slice(0, MAX_SERIES).map(({ group }) => ({
    key: group.codes.join("|"),
    label: group.label,
    valueAt: (bucket: Bucket) => sumAt(group.codes, bucket)
  }));

  const rest = ([] as string[]).concat(...totals.slice(MAX_SERIES).map((t) => t.group.codes));
  if (rest.length > 0) {
    named.push({
      key: "other",
      label: "Other languages",
      valueAt: (bucket: Bucket) => sumAt(rest, bucket)
    });
  }

  // A meeting where every leg carries the same language does not need a per-language split:
  // one series named after the only language is the same column as "lines at the mark".
  return named.length <= 1 ? [] : named;
}

/**
 * The listen-in chart.
 *
 * `role="img"` with a title and a description, and the table below repeats every value --
 * both, not either. The description is what a screen reader hears without leaving the
 * chart; the table is what anyone can read the numbers out of.
 */
function lineChart(
  uid: string,
  grid: TimelineGrid,
  drawn: Series[],
  interval: IntervalMinutes
): HTMLElement {
  const titleId = uid + "-listen-chart-t";
  const descId = uid + "-listen-chart-d";
  const width = 720;
  const height = 258;
  const left = 40;
  const right = 706;
  const top = 16;
  const bottom = 222;

  const count = grid.buckets.length;
  const highest = drawn.reduce((most, s) =>
    grid.buckets.reduce((inner, bucket) => Math.max(inner, s.valueAt(bucket)), most), 0);
  const ceiling = Math.max(10, Math.ceil(highest / 10) * 10);

  const x = (index: number) => count < 2 ? left : left + (right - left) * index / (count - 1);
  const y = (value: number) => bottom - (bottom - top) * (value / ceiling);

  const root = document.createElementNS(SVG_NS, "svg");
  root.setAttribute("viewBox", "0 0 " + width + " " + height);
  root.setAttribute("role", "img");
  root.setAttribute("preserveAspectRatio", "xMidYMid meet");
  root.setAttribute("focusable", "false");
  root.setAttribute("aria-labelledby", titleId + " " + descId);

  const title = document.createElementNS(SVG_NS, "title");
  title.setAttribute("id", titleId);
  title.textContent = "Listen-in lines " + phrase(interval);
  root.appendChild(title);

  const desc = document.createElementNS(SVG_NS, "desc");
  desc.setAttribute("id", descId);
  desc.textContent = chartDescription(grid, drawn, interval);
  root.appendChild(desc);

  for (const value of [ceiling, ceiling * 2 / 3, ceiling / 3]) {
    root.appendChild(svgLine("spr-mc-grid", left, y(value), right, y(value)));
    root.appendChild(svgText("spr-mc-label", 32, y(value) + 3, String(Math.round(value)), "end"));
  }
  root.appendChild(svgLine("spr-mc-axis", left, bottom, right, bottom));
  root.appendChild(svgText("spr-mc-label", 32, bottom + 3, "0", "end"));

  const every = Math.max(1, Math.ceil(count / 12));
  grid.buckets.forEach((bucket, index) => {
    if (index % every !== 0) return;
    root.appendChild(svgText("spr-mc-label", x(index), 242, bucket.markLabel, "middle"));
  });

  drawn.forEach((s, index) => {
    const cls = "spr-mc-series-" + Math.min(index + 1, MAX_SERIES + 1);
    const points = grid.buckets
      .map((bucket, i) => x(i).toFixed(1) + "," + y(s.valueAt(bucket)).toFixed(1))
      .join(" ");

    const line = document.createElementNS(SVG_NS, "polyline");
    line.setAttribute("class", cls);
    line.setAttribute("points", points);
    root.appendChild(line);

    grid.buckets.forEach((bucket, i) => {
      const dot = document.createElementNS(SVG_NS, "circle");
      dot.setAttribute("class", cls + " spr-mc-dot");
      dot.setAttribute("cx", x(i).toFixed(1));
      dot.setAttribute("cy", y(s.valueAt(bucket)).toFixed(1));
      dot.setAttribute("r", "2.6");
      root.appendChild(dot);
    });
  });

  const holder = el("div", { class: "spr-chart", "data-testid": "speaker-report-listen-chart" });
  holder.appendChild(root);
  return holder;
}

function chartDescription(
  grid: TimelineGrid,
  series: Series[],
  interval: IntervalMinutes
): string {
  const first = grid.buckets[0];
  const last = grid.buckets[grid.buckets.length - 1];
  const range = clockMeridiem(first.startMs, grid.offsetMinutes)
    + " to " + clockMeridiem(last.startMs, grid.offsetMinutes);

  const lines = series.map((s) =>
    s.label + ": " + grid.buckets.map((bucket) => s.valueAt(bucket)).join(", "));

  return (series.length === 1 ? "One series" : series.length + " series")
    + " over " + grid.buckets.length + " marks, " + phrase(interval)
    + ", from " + range + ". " + lines.join(". ")
    + ". The table below repeats every value.";
}

function svgLine(cls: string, x1: number, y1: number, x2: number, y2: number): SVGElement {
  const node = document.createElementNS(SVG_NS, "line");
  node.setAttribute("class", cls);
  node.setAttribute("x1", x1.toFixed(1));
  node.setAttribute("y1", y1.toFixed(1));
  node.setAttribute("x2", x2.toFixed(1));
  node.setAttribute("y2", y2.toFixed(1));
  return node;
}

function svgText(cls: string, x: number, y: number, text: string, anchor: string): SVGElement {
  const node = document.createElementNS(SVG_NS, "text");
  node.setAttribute("class", cls);
  node.setAttribute("x", x.toFixed(1));
  node.setAttribute("y", y.toFixed(1));
  node.setAttribute("text-anchor", anchor);
  node.textContent = text;
  return node;
}

// -- shared -----------------------------------------------------------------

/**
 * What the channel column does and does not know, stated wherever a channel is drawn.
 *
 * Channel is the REGISTRATION's answer, not the bridge's observation, and *3 is the only
 * signal the bridge records about intent to speak. A reader who assumes otherwise reads
 * "By phone" as "called in", which is a different fact.
 */
export function channelCoverageNote(timeline: Timeline | undefined): string {
  const base = "Channel is taken from the registration (in person or by phone). "
    + "Phone request-to-speak (*3) is recorded when pressed.";

  if (!timeline) return base;

  const raised = (timeline.connections ?? []).filter((c) => !!c.raisedHandUtc).length;
  return base + " " + (raised > 0
    ? integer(raised) + " *3 " + (raised === 1 ? "request" : "requests") + " recorded."
    : "No *3 request was recorded for this meeting.");
}

function numCell(value: number, extra?: string): HTMLElement {
  return el("div", {
    class: "spr-cell spr-num" + (extra ? " " + extra : ""),
    role: "gridcell",
    text: integer(value)
  });
}

function headCell(text: string, numeric = false): HTMLElement {
  return el("div", {
    class: "spr-cell spr-cell-head" + (numeric ? " spr-num" : ""),
    role: "columnheader",
    text
  });
}

/** "1 item in X" / "15 items in X". The prototype hard-coded the plural. */
function itemsIn(count: number, section: string): string {
  return count + (count === 1 ? " item in " : " items in ") + section;
}

function chip(text: string, cls: string): HTMLElement {
  return el("span", { class: "spr-chip " + cls, text });
}

function swatch(cls: string, label: string): HTMLElement {
  return el("li", { class: "spr-legend-item" }, [
    el("span", { class: "spr-legend-swatch " + cls, "aria-hidden": "true" }),
    document.createTextNode(label)
  ]);
}
